import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import EmployeeLayout from '../../components/layout/EmployeeLayout';
import SupervisorLayout from '../../components/layout/SupervisorLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import StaffMessagesThread from '../../components/StaffMessagesThread';
import {
  User, Mail, Phone, Home, Users, Heart, CalendarDays, Hash, CreditCard, FileText, Save, Send, AlertTriangle, CheckCircle2, Briefcase, Shield,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import ComplianceDocuments from '../../components/ComplianceDocuments';
import EmploymentContract from '../../components/EmploymentContract';
import OnboardingBanner from '../../components/OnboardingBanner';
import PushToggleButton from '../../components/PushToggleButton';
import { useOnboarding } from '../../contexts/OnboardingContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Field({ icon: Icon, label, name, value, onChange, type = 'text', placeholder = '', readonly = false, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </Label>
      {readonly ? (
        <div className="h-10 px-3 flex items-center text-sm bg-muted/30 border border-border rounded-sm" data-testid={testid}>{value || '—'}</div>
      ) : (
        <Input
          type={type}
          name={name}
          value={value || ''}
          onChange={onChange}
          placeholder={placeholder}
          className="rounded-sm"
          data-testid={testid}
        />
      )}
    </div>
  );
}

export default function MyProfilePage() {
  const location = useLocation();
  const VALID_TABS = ['personal', 'bank', 'documents', 'messages'];
  const urlTab = new URLSearchParams(location.search).get('tab');
  const [activeTab, setActiveTab] = useState(VALID_TABS.includes(urlTab) ? urlTab : 'personal');
  useEffect(() => {
    if (urlTab && VALID_TABS.includes(urlTab)) setActiveTab(urlTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlTab]);
  const { user } = useAuth();
  const Layout = user?.role === 'supervisor' ? SupervisorLayout : EmployeeLayout;
  const [profile, setProfile] = useState(null);
  const [bank, setBank] = useState(null);
  const [emailReq, setEmailReq] = useState(null);
  const [loading, setLoading] = useState(true);

  const [editPersonal, setEditPersonal] = useState({});
  const [savingPersonal, setSavingPersonal] = useState(false);

  const [editBank, setEditBank] = useState({});
  const [savingBank, setSavingBank] = useState(false);

  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailSubmitting, setEmailSubmitting] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pr, bk] = await Promise.all([
        axios.get(`${API}/users/me/profile`),
        axios.get(`${API}/users/me/bank`),
      ]);
      setProfile(pr.data);
      setBank(bk.data);
      setEmailReq(null);
      setEditPersonal({
        name: pr.data.name || '',
        phone: pr.data.phone || '',
        home_address: pr.data.home_address || '',
        emergency_contact_name: pr.data.emergency_contact_name || '',
        emergency_contact_phone: pr.data.emergency_contact_phone || '',
        emergency_contact_number: pr.data.emergency_contact_number || '',
        next_of_kin: pr.data.next_of_kin || '',
        dob: pr.data.dob || '',
        ni_number: pr.data.ni_number || '',
        // Extended candidate-application fields — all editable by the
        // employee from this page.
        first_name: pr.data.first_name || '',
        middle_name: pr.data.middle_name || '',
        surname: pr.data.surname || '',
        gender: pr.data.gender || '',
        date_of_birth: pr.data.date_of_birth || '',
        sia_badge_number: pr.data.sia_badge_number || '',
        sia_badge_expiry_date: pr.data.sia_badge_expiry_date || '',
        address: pr.data.address || '',
        city: pr.data.city || '',
        postcode: pr.data.postcode || '',
        id_document_type: pr.data.id_document_type || '',
        id_document_number: pr.data.id_document_number || '',
        id_expiry_date: pr.data.id_expiry_date || '',
        id_issue_date: pr.data.id_issue_date || '',
        id_country_of_issue: pr.data.id_country_of_issue || '',
        current_employment_status: pr.data.current_employment_status || '',
        bank_details: pr.data.bank_details || '',
      });
      setEditBank({
        account_holder: bk.data.account_holder || '',
        sort_code: bk.data.sort_code || '',
        account_number: bk.data.account_number || '',
      });
    } catch (e) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const savePersonal = async () => {
    setSavingPersonal(true);
    try {
      const res = await axios.put(`${API}/users/me/profile`, editPersonal);
      setProfile(res.data);
      toast.success('Profile saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSavingPersonal(false);
    }
  };

  const saveBank = async () => {
    setSavingBank(true);
    try {
      await axios.put(`${API}/users/me/bank`, editBank);
      toast.success('Bank details saved');
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSavingBank(false);
    }
  };

  const submitEmailRequest = async () => {
    // Email change requests were removed 2026-02. Employees now contact
    // Admin via WhatsApp to update their email.
    toast.error('Please contact Admin via WhatsApp on +44 7365 928174 to change your email.');
    setEmailModalOpen(false);
  };

  if (loading || !profile) {
    return (
      <Layout>
        <div className="p-6 text-center text-muted-foreground animate-pulse">Loading your profile…</div>
      </Layout>
    );
  }

  const pendingEmail = emailReq && emailReq.status === 'pending';
  const rejectedEmail = emailReq && emailReq.status === 'rejected';

  return (
    <Layout>
      <div className="p-4 sm:p-6 space-y-6 max-w-5xl mx-auto" data-testid="my-profile-page">
        <OnboardingBanner />
        <div className="border-b border-border pb-6">
          <h1 className="text-2xl font-barlow tracking-widest flex items-center gap-3">
            <User className="w-6 h-6 text-primary" /> MY PROFILE
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your personal details, banking and compliance documents.</p>
        </div>

        {profile?.employee_code && (
          <div className="flex items-center gap-3 rounded-sm border border-primary/50 bg-primary/10 px-4 py-3" data-testid="employee-code-banner">
            <Hash className="w-5 h-5 text-primary flex-shrink-0" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Your Employee Code</p>
              <p className="text-2xl font-bold tracking-[0.35em] text-primary font-mono" data-testid="employee-code-value">{profile.employee_code}</p>
            </div>
            <p className="ml-auto text-[11px] text-muted-foreground max-w-[220px] text-right hidden sm:block">
              Memorise this code — it identifies you for clock in/out and with the office.
            </p>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid grid-cols-4 w-full sm:w-auto sm:inline-grid bg-card border border-border rounded-sm h-auto">
            <TabsTrigger value="personal" className="rounded-sm uppercase tracking-wider text-xs" data-testid="tab-personal">Personal</TabsTrigger>
            <TabsTrigger value="bank" className="rounded-sm uppercase tracking-wider text-xs" data-testid="tab-bank">Bank Details</TabsTrigger>
            <TabsTrigger value="documents" className="rounded-sm uppercase tracking-wider text-xs" data-testid="tab-documents">Documents</TabsTrigger>
            <TabsTrigger value="messages" className="rounded-sm uppercase tracking-wider text-xs" data-testid="tab-messages">Messages</TabsTrigger>
          </TabsList>

          {/* ============ MESSAGES ============ */}
          <TabsContent value="messages" className="space-y-4">
            <StaffMessagesThread viewer="employee" />
          </TabsContent>

          {/* ============ PERSONAL ============ */}
          <TabsContent value="personal" className="space-y-4">
            <PushToggleButton />
            <Card className="bg-card border-border rounded-sm">
              <CardHeader className="border-b border-border pb-4">
                <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
                  <User className="w-4 h-4 text-primary" /> Personal Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                {/* Email — read-only. To change email, contact Admin via WhatsApp. */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2 space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1.5">
                      <Mail className="w-3 h-3" /> Email
                    </Label>
                    <div className="flex-1 min-w-[200px] h-10 px-3 flex items-center text-sm bg-muted/30 border border-border rounded-sm">{profile.email}</div>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      To update your email, please contact Admin via WhatsApp on{' '}
                      <a href="https://wa.me/447365928174" target="_blank" rel="noopener noreferrer" className="underline text-primary">+44 7365 928174</a>.
                    </p>
                  </div>

                  <Field icon={User}  label="Full Name" name="name"  value={editPersonal.name}  onChange={(e) => setEditPersonal({...editPersonal, name: e.target.value})}  testid="name-input" />
                  <Field icon={Phone} label="Phone"     name="phone" value={editPersonal.phone} onChange={(e) => setEditPersonal({...editPersonal, phone: e.target.value})} testid="phone-input" />

                  <div className="sm:col-span-2">
                    <Field icon={Home} label="Home Address" name="home_address" value={editPersonal.home_address} onChange={(e) => setEditPersonal({...editPersonal, home_address: e.target.value})} testid="address-input" placeholder="Street, City, Postcode" />
                  </div>

                  <Field icon={Users} label="Emergency Contact Name"  name="emergency_contact_name"  value={editPersonal.emergency_contact_name}  onChange={(e) => setEditPersonal({...editPersonal, emergency_contact_name: e.target.value})}  testid="ec-name-input" />
                  <Field icon={Phone} label="Emergency Contact Phone" name="emergency_contact_phone" value={editPersonal.emergency_contact_phone} onChange={(e) => setEditPersonal({...editPersonal, emergency_contact_phone: e.target.value})} testid="ec-phone-input" />

                  <Field icon={Heart}        label="Next of Kin"            name="next_of_kin" value={editPersonal.next_of_kin} onChange={(e) => setEditPersonal({...editPersonal, next_of_kin: e.target.value})} testid="nok-input" />
                  <Field icon={CalendarDays} label="Date of Birth"          name="dob"         value={editPersonal.dob}         onChange={(e) => setEditPersonal({...editPersonal, dob: e.target.value})}         testid="dob-input" type="date" />
                  <Field icon={Hash}         label="National Insurance No." name="ni_number"   value={editPersonal.ni_number}   onChange={(e) => setEditPersonal({...editPersonal, ni_number: e.target.value})}   testid="ni-input" placeholder="AB 12 34 56 C" />

                  <Field icon={Hash} label="Employee Code" name="employee_code" value={profile.employee_code} readonly />
                  <Field
                    icon={Briefcase}
                    label="Position"
                    name="position"
                    value={profile.position || 'Not set — ask your administrator'}
                    readonly
                    testid="position-readonly"
                  />
                </div>

                {/* Extended candidate-application fields — every employee
                    can view and edit their own. Only own profile is
                    accessible because the API uses /users/me/profile. */}
                <div className="pt-4 border-t border-border space-y-4">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Application Details</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field icon={User}          label="First name"   name="first_name"   value={editPersonal.first_name}   onChange={(e) => setEditPersonal({...editPersonal, first_name: e.target.value})}   testid="first-name-input" />
                    <Field icon={User}          label="Middle name"  name="middle_name"  value={editPersonal.middle_name}  onChange={(e) => setEditPersonal({...editPersonal, middle_name: e.target.value})}  testid="middle-name-input" />
                    <Field icon={User}          label="Surname"      name="surname"      value={editPersonal.surname}      onChange={(e) => setEditPersonal({...editPersonal, surname: e.target.value})}      testid="surname-input" />
                    <Field icon={User}          label="Gender"       name="gender"       value={editPersonal.gender}       onChange={(e) => setEditPersonal({...editPersonal, gender: e.target.value})}       testid="gender-input" />
                    <Field icon={CalendarDays}  label="Date of birth" name="date_of_birth" value={editPersonal.date_of_birth} onChange={(e) => setEditPersonal({...editPersonal, date_of_birth: e.target.value})} testid="date-of-birth-input" type="date" />

                    {/* SIA fields are only relevant for Door Supervisor (SIA)
                        staff. We hide them exclusively for confirmed CSO
                        Stewards — for legacy users whose `position` was
                        never populated (invited before we started copying
                        position from the application to the user row) we
                        DO show the fields, since we can't safely assume
                        they aren't SIA. Backend already excludes SIA
                        fields from onboarding evaluation for CSO Stewards. */}
                    {profile?.position !== 'CSO Steward' && (
                      <>
                        <Field icon={Shield}        label="SIA Badge number"      name="sia_badge_number"      value={editPersonal.sia_badge_number}      onChange={(e) => setEditPersonal({...editPersonal, sia_badge_number: e.target.value})}      testid="sia-badge-number-input" />
                        <Field icon={CalendarDays}  label="SIA Badge expiry date" name="sia_badge_expiry_date" value={editPersonal.sia_badge_expiry_date} onChange={(e) => setEditPersonal({...editPersonal, sia_badge_expiry_date: e.target.value})} testid="sia-badge-expiry-input" type="date" />
                      </>
                    )}

                    <div className="sm:col-span-2">
                      <Field icon={Home} label="Address" name="address" value={editPersonal.address} onChange={(e) => setEditPersonal({...editPersonal, address: e.target.value})} testid="address-line-input" placeholder="Street" />
                    </div>
                    <Field icon={Home}          label="City"      name="city"      value={editPersonal.city}      onChange={(e) => setEditPersonal({...editPersonal, city: e.target.value})}      testid="city-input" />
                    <Field icon={Hash}          label="Postcode"  name="postcode"  value={editPersonal.postcode}  onChange={(e) => setEditPersonal({...editPersonal, postcode: e.target.value})}  testid="postcode-input" />

                    <Field icon={FileText}      label="ID Document type"     name="id_document_type"    value={editPersonal.id_document_type}    onChange={(e) => setEditPersonal({...editPersonal, id_document_type: e.target.value})}    testid="id-doc-type-input"   placeholder="Passport / BRP / Driving Licence" />
                    <Field icon={Hash}          label="ID Document number"   name="id_document_number"  value={editPersonal.id_document_number}  onChange={(e) => setEditPersonal({...editPersonal, id_document_number: e.target.value})}  testid="id-doc-number-input" />
                    <Field icon={CalendarDays}  label="ID expiry date"       name="id_expiry_date"      value={editPersonal.id_expiry_date}      onChange={(e) => setEditPersonal({...editPersonal, id_expiry_date: e.target.value})}      testid="id-expiry-input"     type="date" />
                    <Field icon={CalendarDays}  label="ID issue date"        name="id_issue_date"       value={editPersonal.id_issue_date}       onChange={(e) => setEditPersonal({...editPersonal, id_issue_date: e.target.value})}       testid="id-issue-input"      type="date" />
                    <Field icon={Home}          label="ID Country of issue"  name="id_country_of_issue" value={editPersonal.id_country_of_issue} onChange={(e) => setEditPersonal({...editPersonal, id_country_of_issue: e.target.value})} testid="id-country-input" />

                    <Field icon={Briefcase}     label="What is your current employment status?" name="current_employment_status" value={editPersonal.current_employment_status} onChange={(e) => setEditPersonal({...editPersonal, current_employment_status: e.target.value})} testid="employment-status-input" />
                    <div className="sm:col-span-2">
                      <Field icon={CreditCard}  label="Bank details" name="bank_details" value={editPersonal.bank_details} onChange={(e) => setEditPersonal({...editPersonal, bank_details: e.target.value})} testid="bank-details-input" placeholder="Bank name, sort code, account number" />
                    </div>

                    <Field icon={Users}         label="EMERGENCY CONTACT NAME"   name="emergency_contact_name"   value={editPersonal.emergency_contact_name}   onChange={(e) => setEditPersonal({...editPersonal, emergency_contact_name: e.target.value})}   testid="emergency-name-input" />
                    <Field icon={Phone}         label="EMERGENCY CONTACT NUMBER" name="emergency_contact_number" value={editPersonal.emergency_contact_number} onChange={(e) => setEditPersonal({...editPersonal, emergency_contact_number: e.target.value})} testid="emergency-number-input" />
                  </div>
                </div>

                <div className="flex justify-end pt-2">
                  <Button onClick={savePersonal} disabled={savingPersonal} className="rounded-sm bg-primary text-primary-foreground hover:bg-yellow-300 uppercase tracking-wider text-xs" data-testid="save-personal-btn">
                    <Save className="w-4 h-4 mr-2" /> {savingPersonal ? 'Saving…' : 'Save changes'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============ BANK ============ */}
          <TabsContent value="bank" className="space-y-4">
            <Card className="bg-card border-border rounded-sm">
              <CardHeader className="border-b border-border pb-4">
                <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-primary" /> Bank Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                <div className="text-xs text-muted-foreground bg-blue-500/10 border border-blue-500/30 rounded-sm p-3">
                  <strong>Privacy:</strong> Bank details are only visible to you and the Admin. Supervisors cannot see them.
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Field icon={User} label="Account Holder Name" name="account_holder" value={editBank.account_holder} onChange={(e) => setEditBank({...editBank, account_holder: e.target.value})} testid="account-holder-input" />
                  </div>
                  <Field icon={Hash} label="Sort Code"      name="sort_code"      value={editBank.sort_code}      onChange={(e) => setEditBank({...editBank, sort_code: e.target.value})}      testid="sort-code-input"      placeholder="XX-XX-XX" />
                  <Field icon={Hash} label="Account Number" name="account_number" value={editBank.account_number} onChange={(e) => setEditBank({...editBank, account_number: e.target.value})} testid="account-number-input" placeholder="8 digits" />
                </div>
                {bank?.updated_at && (
                  <p className="text-xs text-muted-foreground">Last updated {new Date(bank.updated_at).toLocaleString('en-GB')}</p>
                )}
                <div className="flex justify-end pt-2">
                  <Button onClick={saveBank} disabled={savingBank} className="rounded-sm bg-primary text-primary-foreground hover:bg-yellow-300 uppercase tracking-wider text-xs" data-testid="save-bank-btn">
                    <Save className="w-4 h-4 mr-2" /> {savingBank ? 'Saving…' : 'Save bank details'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============ DOCUMENTS ============ */}
          <TabsContent value="documents" className="space-y-4">
            <EmploymentContract mode="employee" />
            <ComplianceDocuments mode="employee" />
          </TabsContent>
        </Tabs>
      </div>

      {/* Email change request modal */}
      <Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
        <DialogContent data-testid="email-change-modal">
          <DialogHeader>
            <DialogTitle>Request email change</DialogTitle>
            <DialogDescription>Your new email will only become active after admin approval.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">New email</Label>
            <Input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="new@example.com" className="rounded-sm" data-testid="new-email-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailModalOpen(false)} disabled={emailSubmitting} className="rounded-sm">Cancel</Button>
            <Button onClick={submitEmailRequest} disabled={emailSubmitting || !newEmail} className="rounded-sm bg-primary text-primary-foreground hover:bg-yellow-300" data-testid="submit-email-request-btn">
              {emailSubmitting ? 'Submitting…' : 'Submit request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
